/**
 * Zijscherm voor een Outlook-afspraak in de Dagstart (Joris, 3 nov 2026: "kan je klikken op een
 * item zodat je scherm krijgt"). Alleen wat de Dagstart al van de afspraak weet: onderwerp
 * (afgeschermd → "Afspraak"/"Privé"), dag, tijd, soort en van wie hij is. Er wordt niets extra
 * uit iemands agenda opgehaald; aanpassen gebeurt in Outlook.
 */
import React from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon, LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { AFSPRAAK_KLEUREN, AFSPRAAK_SOORT_TEKST } from './TaskBlock';
import { parseISODate } from '../../utils/weekUtils';

const GEDEMPT = '#64748b';

const dagTekst = (iso) => {
  const d = parseISODate(String(iso || '').slice(0, 10));
  return d ? d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
};

const tijdVan = (t) => t.tijd || (t.startTime ? `${t.startTime}${t.endTime ? `–${t.endTime}` : ''}` : '');

/** Wat er op het scherm komt; los te testen. */
export function afspraakWeergave(taak, items) {
  const lijst = (items && items.length ? items : [taak]).filter(Boolean);
  const afgeschermd = Boolean(taak?.afgeschermd);
  return {
    titel: afgeschermd ? (taak?.prive ? 'Privé' : 'Afspraak') : (taak?.name || 'Afspraak'),
    afgeschermd,
    soort: AFSPRAAK_SOORT_TEKST[taak?.afspraakSoort] || '',
    kleur: AFSPRAAK_KLEUREN[taak?.afspraakSoort] || AFSPRAAK_KLEUREN.intern,
    persoon: taak?.assignee || '',
    momenten: lijst.map((t) => ({ dag: dagTekst(t.startDate), tijd: tijdVan(t), uren: Number(t.hours) || 0 })),
  };
}

export default function AfspraakPaneel({ open, taak, items, onClose }) {
  const w = taak ? afspraakWeergave(taak, items) : null;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, p: 3 } }}>
      {w && (
        <Box data-testid="afspraak-paneel">
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 2, bgcolor: w.kleur, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: GEDEMPT }}>
                Afspraak{w.soort ? ` · ${w.soort}` : ''}
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3, overflowWrap: 'anywhere', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {w.afgeschermd && <LockOutlinedIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
                {w.titel}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Sluiten"><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ display: 'grid', gap: 1 }}>
            {w.momenten.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, fontSize: 14 }}>
                <Typography sx={{ fontSize: 14, flex: 1, textTransform: 'capitalize' }}>{m.dag}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {m.tijd || (m.uren ? `${m.uren} u` : 'hele dag')}
                </Typography>
              </Box>
            ))}
            {w.persoon && (
              <Typography sx={{ fontSize: 13, color: GEDEMPT, mt: 1 }}>In de agenda van {w.persoon}</Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 12, color: GEDEMPT, mt: 3 }}>
            {w.afgeschermd
              ? 'Het onderwerp is afgeschermd; alleen de tijd telt mee in de bezetting.'
              : 'Uit Outlook. Aanpassen of verplaatsen doe je daar.'}
          </Typography>
        </Box>
      )}
    </Drawer>
  );
}
